import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";

const LiveUpdatesContext = createContext({ connected: false });

function socketBase() {
  const backend = process.env.REACT_APP_BACKEND_URL;
  return backend.replace(/^http:/, "ws:").replace(/^https:/, "wss:");
}

export function LiveUpdatesProvider({ children }) {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem("kloss_token");
    if (!user || !token) return undefined;
    let socket; let retry; let heartbeat; let disposed = false;
    const connect = () => {
      if (disposed) return;
      socket = new WebSocket(`${socketBase()}/api/realtime/ws?token=${encodeURIComponent(token)}`);
      socket.onopen = () => {
        if (disposed) { socket.close(1000, "cleanup"); return; }
        setConnected(true);
        heartbeat = window.setInterval(() => { if (socket.readyState === WebSocket.OPEN) socket.send("ping"); }, 25000);
      };
      socket.onmessage = (event) => { if (disposed) return; try { const payload = JSON.parse(event.data); if (payload.type === "data_updated") window.dispatchEvent(new CustomEvent("kloss-live-update", { detail: payload })); if (payload.type === "treatment_alert") window.dispatchEvent(new CustomEvent("kloss-treatment-alert", { detail: payload })); } catch (_) {} };
      socket.onclose = () => { if (disposed) return; setConnected(false); window.clearInterval(heartbeat); retry = window.setTimeout(connect, 3000); };
      socket.onerror = () => { if (!disposed) setConnected(false); };
    };
    connect();
    return () => {
      disposed = true;
      setConnected(false);
      window.clearInterval(heartbeat);
      window.clearTimeout(retry);
      if (!socket) return;
      socket.onclose = null;
      socket.onerror = null;
      socket.onmessage = null;
      if (socket.readyState === WebSocket.CONNECTING) socket.onopen = () => socket.close(1000, "cleanup");
      else if (socket.readyState === WebSocket.OPEN) socket.close(1000, "cleanup");
    };
  }, [user]);
  return <LiveUpdatesContext.Provider value={{ connected }}>{children}</LiveUpdatesContext.Provider>;
}

export function useLiveRefresh(areas, refresh) {
  const refreshRef = useRef(refresh);
  const areasRef = useRef(areas);
  const areasKey = areas.join("|");
  areasRef.current = areas;
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);
  useEffect(() => {
    const listener = (event) => {
      const changed = event.detail?.areas || [];
      if (areasRef.current.some((area) => changed.includes(area))) refreshRef.current?.();
    };
    window.addEventListener("kloss-live-update", listener);
    return () => window.removeEventListener("kloss-live-update", listener);
  }, [areasKey]);
}

export const useLiveUpdates = () => useContext(LiveUpdatesContext);